import type { UserQuestionAnswer } from '@/frontend/pages/Dashboard/AgentChats/types'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { router, usePage } from '@inertiajs/react'
import { PanelLeft } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ConversationPanel } from '@/frontend/pages/Dashboard/AgentChats/components/ConversationPanel'
import { AssistantSessionList } from '@/frontend/features/AgentChats/assistant-ui/components/AssistantSessionList'
import { backgroundWorkItems } from '@/frontend/features/AgentChats/tools/BackgroundWorkCard'
import { publishAgentChatSidebar } from '@/frontend/pages/Dashboard/AgentChats/agentChatSidebarStore'
import { isAssistantUiEnabled, persistedUiParam } from '@/frontend/pages/Dashboard/AgentChats/runtime/assistantUiToggle'
import { validateAgentChatFiles } from '@/frontend/pages/Dashboard/AgentChats/attachments'
import { useAgentChatController } from '@/frontend/pages/Dashboard/AgentChats/useAgentChatController'
import { chatUnavailableReason } from '@/frontend/layouts/worker-first/chatGate'
import type {
  AgentChatDigitalWorker,
  AgentChatParallelWorkSummary,
  AgentChatProject,
  AgentChatSessionDetail,
  AgentChatSessionSummary,
  AgentChatWorkspaceProject,
} from '@/frontend/pages/Dashboard/AgentChats/types'
import type { DigitalWorkerDetail } from '@/frontend/pages/Dashboard/DigitalWorkers/types'
import type { SharedProps } from '@/frontend/types/inertia'
import SectionUnavailableState, { type SectionUnavailableReason } from '@/frontend/components/DigitalWorkers/SectionUnavailableState'
import { cn } from '@/lib/utils'
import type { RunActivity } from '@/frontend/features/AgentChats/events/agentEvents'
import { latestContextUsage } from '@/frontend/features/AgentChats/assistant-ui/contextUsage'

const AssistantConversationPanel = React.lazy(() =>
  import('@/frontend/features/Fde/LiveAssistantConversationPanel')
    .then((module) => ({ default: module.LiveAssistantConversationPanel })),
)

interface Props {
  // The live portal worker prop (drives the gate on provisioning poll).
  worker: DigitalWorkerDetail
  organizationId: string | null
  // Chat-shaped worker (can_send / agent identity) — null while gated.
  chatWorker: AgentChatDigitalWorker | null
  // A sessão inicial null já codifica "nova conversa" (o controller não resolve
  // a mais recente quando ?new_conversation=1), então o useAgentChatController
  // deriva isNewConversation de session === null — sem flag redundante.
  session: AgentChatSessionDetail | null
  sessions: AgentChatSessionSummary[]
  projects: AgentChatProject[]
  workspaceProjects: AgentChatWorkspaceProject[]
  workspaceProjectsLoaded: boolean
  // The dedicated Chat route inherits a bounded flex slot from the dashboard.
  // Combined layouts such as onboarding need their own explicit fallback height.
  fillAvailableHeight?: boolean
}

const CHAT_SIDEBAR_COOKIE_PREFIX = 'stationzero_chat_conversations_expanded'

function chatSidebarCookieName(userId: string | number | null | undefined): string {
  return `${CHAT_SIDEBAR_COOKIE_PREFIX}_${userId ?? 'anonymous'}`
}

function readChatSidebarCookie(userId: string | number | null | undefined): boolean {
  if (typeof document === 'undefined') return true

  const name = `${encodeURIComponent(chatSidebarCookieName(userId))}=`
  const value = document.cookie
    .split('; ')
    .find((entry) => entry.startsWith(name))
    ?.slice(name.length)
  return value === undefined ? true : value === 'true'
}

function writeChatSidebarCookie(userId: string | number | null | undefined, expanded: boolean): void {
  if (typeof document === 'undefined') return

  const name = encodeURIComponent(chatSidebarCookieName(userId))
  document.cookie = `${name}=${expanded}; Path=/; Max-Age=31536000; SameSite=Lax`
}

/**
 * Chat do portal: UMA superfície integra o histórico e a conversa, enquanto a
 * moldura da página detém identidade e disponibilidade do agente. No desktop,
 * AssistantSessionList e a thread dividem todo o card; em larguras compactas,
 * um controle no topo do card alterna os painéis sem criar outra superfície.
 *
 * O runtime assistant-ui, o fluxo de envio/streaming e `?ui=legacy` seguem
 * intactos. No desktop, a altura cresce pelo flex layout até o fim da área
 * útil; cada painel rola por dentro do container.
 *
 * Estados: sem instância pronta (gate SZ-1583: ready + probe) a aba explica
 * in-place (SectionUnavailableState, o mesmo tratamento das seções gated);
 * pronta sem conversa → o thread vazio já traz o composer "comece uma conversa".
 */
export default function WorkerChatTab({
  worker,
  organizationId,
  chatWorker,
  session: initialSession,
  sessions: initialSessions,
  projects: initialProjects,
  workspaceProjects,
  workspaceProjectsLoaded,
  fillAvailableHeight = false,
}: Props) {
  const { auth } = usePage<SharedProps>().props
  const currentUserName = auth.user?.name || null
  const [useAssistantUi] = useState(isAssistantUiEnabled)
  const [draft, setDraft] = useState('')
  const [attachments, setAttachments] = useState<File[]>([])
  const [mobileSessionsOpen, setMobileSessionsOpen] = useState(false)
  const [chatSidebarExpanded, setChatSidebarExpanded] = useState(
    () => readChatSidebarCookie(auth.user?.id),
  )
  const recoveredDraftTokenRef = useRef<symbol | null>(null)
  const initialWorkspaceProjectId = initialSession?.fde_project_id || (
    typeof window === 'undefined'
      ? null
      : new URLSearchParams(window.location.search).get('fde_project_id')
  )
  // Optional project lists hydrate later. Keep the requested scope while they
  // load: an early send must be authorized as a project turn, never silently
  // fall back to a general chat. Rails validates assignment on every creation.
  const [selectedWorkspaceProjectId, setSelectedWorkspaceProjectId] = useState<string | null>(initialWorkspaceProjectId)
  const selectedWorkspaceProjectIdRef = useRef(selectedWorkspaceProjectId)
  const requestedWorkspaceProjectIdRef = useRef(initialWorkspaceProjectId)
  const desktopHeightClasses = fillAvailableHeight
    ? 'md:h-auto md:min-h-0 md:flex-1'
    : 'md:h-[min(60rem,calc(100dvh-15rem))] md:min-h-0 md:max-h-[60rem]'

  useEffect(() => {
    setChatSidebarExpanded(readChatSidebarCookie(auth.user?.id))
  }, [auth.user?.id])

  const toggleChatSidebar = useCallback(() => {
    setChatSidebarExpanded((expanded) => {
      const next = !expanded
      writeChatSidebarCookie(auth.user?.id, next)
      return next
    })
  }, [auth.user?.id])

  // Gate do chat com o worker VIVO (segue o poll de provisioning): sem bridge
  // pronto a aba explica o motivo, como as demais seções gated do portal. Só
  // depende da prop `worker` (não de chatWorker), então o poll pode virar a
  // página para o chat real sem full reload.
  const gateReason = chatUnavailableReason(worker)

  const updateUrlForSession = useCallback((sessionId: string | null) => {
    if (typeof window === 'undefined') return

    const params = new URLSearchParams()
    if (sessionId) {
      params.set('session_id', sessionId)
    } else {
      params.set('new_conversation', '1')
      if (selectedWorkspaceProjectIdRef.current) {
        params.set('fde_project_id', selectedWorkspaceProjectIdRef.current)
      }
    }
    const ui = persistedUiParam()
    if (ui) params.set('ui', ui)

    const query = params.toString()
    // "Nova conversa" is an explicit local choice. Cancel a pending Inertia
    // handoff before updating history so an older response cannot replace the
    // blank composer (and any draft started in it) after this click.
    if (!sessionId) router.cancel()
    window.history.pushState(
      window.history.state,
      '',
      `/dashboard/digital_workers/${worker.id}/chat${query ? `?${query}` : ''}`,
    )
  }, [worker.id])

  const chat = useAgentChatController({
    organizationId,
    digitalWorker: chatWorker,
    initialSession,
    initialSessions,
    initialProjects,
    sessionCreateSource: selectedWorkspaceProjectId ? 'fde_project' : undefined,
    sessionCreateFdeProjectId: selectedWorkspaceProjectId ?? undefined,
    onSessionChange: updateUrlForSession,
    onReconnect: () => {
      router.reload({ only: ['chat_session', 'chat_sessions', 'chat_projects', 'chat_workspace_projects'] })
    },
  })

  useEffect(() => {
    if (!chat.session) return

    const projectId = chat.session.fde_project_id ?? null
    requestedWorkspaceProjectIdRef.current = null
    selectedWorkspaceProjectIdRef.current = projectId
    setSelectedWorkspaceProjectId(projectId)
  }, [chat.session?.fde_project_id, chat.session?.id, workspaceProjects])

  useEffect(() => {
    const requestedProjectId = requestedWorkspaceProjectIdRef.current
    if (chat.session || !requestedProjectId || !workspaceProjectsLoaded) return

    const projectId = workspaceProjects.some((project) => project.id === requestedProjectId)
      ? requestedProjectId
      : null
    requestedWorkspaceProjectIdRef.current = null
    selectedWorkspaceProjectIdRef.current = projectId
    setSelectedWorkspaceProjectId(projectId)
  }, [chat.session, workspaceProjects, workspaceProjectsLoaded])

  // Optional Inertia props arrive after the blank composer mounts. Validate
  // only after that authoritative list has loaded, including an empty list
  // after the last assignment is revoked.
  useEffect(() => {
    if (chat.session || !workspaceProjectsLoaded || requestedWorkspaceProjectIdRef.current) return
    if (!selectedWorkspaceProjectId) return
    if (workspaceProjects.some((project) => project.id === selectedWorkspaceProjectId)) return

    selectedWorkspaceProjectIdRef.current = null
    setSelectedWorkspaceProjectId(null)
  }, [chat.session, selectedWorkspaceProjectId, worker.id, workspaceProjects, workspaceProjectsLoaded])

  const selectWorkspaceProject = useCallback((projectId: string | null) => {
    if (!chat.canStartNewConversation) return
    if (projectId && !workspaceProjects.some((project) => project.id === projectId)) return

    selectedWorkspaceProjectIdRef.current = projectId
    setSelectedWorkspaceProjectId(projectId)
    chat.handleNewConversation()
  }, [chat.canStartNewConversation, chat.handleNewConversation, workspaceProjects])
  const sidebarSessions = useMemo(
    () => sessionsWithLiveParallelWork(chat.sessions, chat.session?.id ?? null, chat.activity),
    [chat.activity, chat.session?.id, chat.sessions],
  )
  const contextUsage = useMemo(
    () => latestContextUsage(chat.activity, chat.session?.messages ?? []),
    [chat.activity, chat.session?.messages],
  )

  useEffect(() => {
    recoveredDraftTokenRef.current = null
    setDraft('')
    setAttachments([])
    setMobileSessionsOpen(false)
  }, [chatWorker?.id, initialSession?.id])

  const handleAddAttachments = useCallback((incomingFiles: File[]) => {
    recoveredDraftTokenRef.current = null
    const result = validateAgentChatFiles(attachments, incomingFiles)
    if (result.error) {
      chat.setAttachmentError(result.error)
      return
    }
    setAttachments(result.files)
    chat.setAttachmentError(null)
  }, [attachments, chat.setAttachmentError])

  const handleRemoveAttachment = useCallback((index: number) => {
    recoveredDraftTokenRef.current = null
    setAttachments((current) => current.filter((_, currentIndex) => currentIndex !== index))
    chat.setAttachmentError(null)
  }, [chat.setAttachmentError])

  const handleDraftChange = useCallback((value: string) => {
    recoveredDraftTokenRef.current = null
    setDraft(value)
  }, [])

  const handleSendMessage = async () => {
    const content = draft.trim()
    if (!content) {
      if (attachments.length > 0) chat.setAttachmentError('Escreva uma mensagem para enviar anexos.')
      return
    }
    if (chat.submissionPhase !== 'idle') return

    const recoveryToken = Symbol()
    const selectedAttachments = attachments
    recoveredDraftTokenRef.current = null
    setDraft('')
    setAttachments([])
    await chat.submitMessage(content, selectedAttachments, (pending) => {
      recoveredDraftTokenRef.current = recoveryToken
      setDraft(pending.draft)
      setAttachments(pending.attachments)
    }, () => {
      if (recoveredDraftTokenRef.current !== recoveryToken) return

      recoveredDraftTokenRef.current = null
      setDraft('')
      setAttachments([])
    })
  }

  // assistant-ui composer entry point: its composer owns the text and files.
  const handleAssistantSubmit = useCallback(async (
    content: string,
    files: File[],
    onRecoveredCommit?: () => void,
    questionAnswer?: UserQuestionAnswer,
  ) => {
    return chat.submitMessage(content, files, undefined, onRecoveredCommit, questionAnswer)
  }, [chat.submitMessage])

  // Publica a lista viva para o grupo do agente na sidebar global (store
  // SZ-1504): a conversa aberta fica marcada e a lista semeia o cache — o
  // MESMO comportamento do chat de hoje, agora a partir da aba do portal.
  useEffect(() => {
    if (!chatWorker) return undefined
    publishAgentChatSidebar({
      activeWorkerId: chatWorker.id,
      workerName: chatWorker.name,
      activeSessionId: chat.isNewConversation ? null : chat.session?.id ?? null,
      isNewConversation: chat.isNewConversation,
      canStartNewConversation: chat.canStartNewConversation,
      contextUsage,
      sessions: sidebarSessions,
      projects: chat.projects,
      onNewConversation: chat.handleNewConversation,
      onRenameSession: chat.renameSession,
      onPinSession: chat.pinSession,
      onArchiveSession: chat.archiveSession,
      onDeleteSession: chat.deleteSession,
      onMoveSessionToProject: chat.moveSessionToProject,
      onCreateProject: chat.createProject,
      onRenameProject: chat.renameProject,
      onDeleteProject: chat.deleteProject,
    })
    return undefined
  }, [
    chatWorker,
    chat.session?.id,
    chat.isNewConversation,
    chat.canStartNewConversation,
    contextUsage,
    sidebarSessions,
    chat.projects,
    chat.handleNewConversation,
    chat.renameSession,
    chat.pinSession,
    chat.archiveSession,
    chat.deleteSession,
    chat.moveSessionToProject,
    chat.createProject,
    chat.renameProject,
    chat.deleteProject,
  ])

  useEffect(() => () => publishAgentChatSidebar(null), [])

  // Gate SZ-1583: a aba explica o motivo in-place, como as outras seções
  // gated. Só "provisionando" (pending/provisioning) usa o ponto pulsante; os
  // demais motivos (falha, stale, ou ready-sem-bridge) caem em
  // "instancia_indisponivel".
  if (gateReason || !chatWorker) {
    const provisioning =
      worker.provisioning_status === 'pending' || worker.provisioning_status === 'provisioning'
    const reason: SectionUnavailableReason = provisioning ? 'provisionando' : 'instancia_indisponivel'
    return (
      <div data-testid="worker-chat-tab">
        <SectionUnavailableState sectionLabel="Chat" workerName={worker.name} reason={reason} />
      </div>
    )
  }

  if (useAssistantUi || chat.session?.source === 'praxis_workflow') {
    return (
      <div
        data-testid="worker-chat-tab"
        className={cn(
          'flex h-[520px] w-full min-w-0',
          desktopHeightClasses,
        )}
      >
        <React.Suspense
          fallback={
            <div
              className="flex h-full items-center justify-center rounded-xl border border-border bg-card text-sm text-muted-foreground"
              data-testid="agent-chat-conversation"
            >
              Carregando conversa…
            </div>
          }
        >
          <AssistantConversationPanel
            error={chat.visibleError}
            attachmentError={chat.attachmentError}
            activity={chat.activity}
            isRunning={chat.assistantIsRunning}
            connectionStatus={chat.connectionStatus}
            digitalWorker={chatWorker}
            session={chat.session}
            currentUserName={currentUserName}
            projects={chat.projects}
            workspaceProjects={workspaceProjects}
            selectedWorkspaceProjectId={selectedWorkspaceProjectId}
            navigation={
              <AssistantSessionList
                activeSessionId={chat.isNewConversation ? null : chat.session?.id ?? null}
                isNewConversation={chat.isNewConversation}
                sessions={sidebarSessions}
                projects={chat.projects}
                workerId={chatWorker.id}
                searchable
                newConversationDisabled={!chat.canStartNewConversation}
                onNavigate={() => setMobileSessionsOpen(false)}
                onNewConversation={chat.handleNewConversation}
                onRenameSession={chat.renameSession}
                onPinSession={chat.pinSession}
                onArchiveSession={chat.archiveSession}
                onDeleteSession={chat.deleteSession}
                onMoveSessionToProject={chat.moveSessionToProject}
                onCreateProject={chat.createProject}
                onRenameProject={chat.renameProject}
                onDeleteProject={chat.deleteProject}
              />
            }
            navigationOpen={mobileSessionsOpen}
            onToggleNavigation={() => setMobileSessionsOpen((current) => !current)}
            navigationCollapsed={!chatSidebarExpanded}
            onToggleNavigationCollapsed={toggleChatSidebar}
            onAttachmentError={chat.setAttachmentError}
            onStop={chat.abortCurrentTurn}
            onRetry={chat.retryFailedTurn}
            onFeedback={chat.submitMessageFeedback}
            onBranch={chat.branchConversation}
            onMoveSessionToProject={chat.moveSessionToProject}
            onSelectWorkspaceProject={selectWorkspaceProject}
            onSubmit={handleAssistantSubmit}
          />
        </React.Suspense>
      </div>
    )
  }

  // Legacy opt-out (?ui=legacy): preserva a composição anterior e seus
  // controles fora do painel sem contaminar o contrato visual principal.
  return (
    <div
      data-testid="worker-chat-tab"
      className={cn(
        'mx-auto flex min-h-[520px] w-full min-w-0 max-w-[96rem] gap-4',
        desktopHeightClasses,
      )}
    >
      <aside
        data-testid="agent-chat-session-rail"
        data-surface="quiet-rail"
        className="hidden w-64 min-w-0 shrink-0 overflow-hidden 2xl:flex 2xl:flex-col"
      >
        <AssistantSessionList
          activeSessionId={chat.isNewConversation ? null : chat.session?.id ?? null}
          isNewConversation={chat.isNewConversation}
          sessions={sidebarSessions}
          projects={chat.projects}
          workerId={chatWorker.id}
          searchable
          newConversationDisabled={!chat.canStartNewConversation}
          onNewConversation={chat.handleNewConversation}
          onRenameSession={chat.renameSession}
          onPinSession={chat.pinSession}
          onArchiveSession={chat.archiveSession}
          onDeleteSession={chat.deleteSession}
          onMoveSessionToProject={chat.moveSessionToProject}
          onCreateProject={chat.createProject}
          onRenameProject={chat.renameProject}
          onDeleteProject={chat.deleteProject}
        />
      </aside>

      <div className="flex min-h-0 min-w-0 max-w-full flex-1 flex-col">
        <div className="shrink-0 pb-2 2xl:hidden">
          <Button
            type="button"
            variant="outline"
            size="sm"
            aria-controls="agent-chat-mobile-sessions"
            aria-expanded={mobileSessionsOpen}
            data-testid="agent-chat-mobile-sessions-toggle"
            onClick={() => setMobileSessionsOpen((current) => !current)}
          >
            <PanelLeft aria-hidden="true" className="size-4" />
            {mobileSessionsOpen ? 'Voltar à conversa' : 'Conversas'}
          </Button>
        </div>

        <div
          id="agent-chat-mobile-sessions"
          data-testid="agent-chat-mobile-sessions"
          className={cn(
            'min-h-0 min-w-0 max-w-full flex-1 overflow-hidden 2xl:hidden',
            !mobileSessionsOpen && 'hidden',
          )}
        >
          <AssistantSessionList
            activeSessionId={chat.isNewConversation ? null : chat.session?.id ?? null}
            isNewConversation={chat.isNewConversation}
            sessions={sidebarSessions}
            projects={chat.projects}
            workerId={chatWorker.id}
            searchable
            newConversationDisabled={!chat.canStartNewConversation}
            onNavigate={() => setMobileSessionsOpen(false)}
            onNewConversation={chat.handleNewConversation}
            onRenameSession={chat.renameSession}
            onPinSession={chat.pinSession}
            onArchiveSession={chat.archiveSession}
            onDeleteSession={chat.deleteSession}
            onMoveSessionToProject={chat.moveSessionToProject}
            onCreateProject={chat.createProject}
            onRenameProject={chat.renameProject}
            onDeleteProject={chat.deleteProject}
          />
        </div>

        <div className={cn('min-h-0 min-w-0 max-w-full flex-1', mobileSessionsOpen && 'hidden 2xl:block')}>
          <div className="flex h-full min-h-0 flex-col">
            <ConversationPanel
              fillContainer
              draft={draft}
              error={chat.visibleError}
              attachmentError={chat.attachmentError}
              attachments={attachments}
              isSending={chat.submissionPhase !== 'idle'}
              isUploadingAttachments={chat.submissionPhase === 'uploading'}
              connectionStatus={chat.connectionStatus}
              digitalWorker={chatWorker}
              session={chat.session}
              currentUserName={currentUserName}
              onDraftChange={handleDraftChange}
              onAddAttachments={handleAddAttachments}
              onRemoveAttachment={handleRemoveAttachment}
              onSendMessage={handleSendMessage}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

function sessionsWithLiveParallelWork(
  sessions: AgentChatSessionSummary[],
  activeSessionId: string | null,
  activity: RunActivity | null,
): AgentChatSessionSummary[] {
  if (!activeSessionId || !activity) return sessions

  const liveItems = backgroundWorkItems(activity).flatMap<AgentChatParallelWorkSummary>((item) => {
    if (!item.runId) return []
    let kind: AgentChatParallelWorkSummary['kind']
    if (item.kind === 'parallel_analysis' || item.kind === 'conductor') kind = 'parallel_analysis'
    else return []

    return [{
      id: item.sourceRef || `${item.kind}:${item.runId}`,
      kind,
      run_id: item.runId,
      turn_id: activity.turnId,
      title: item.expectedDeliverable || item.title || 'Trabalho em paralelo',
      status: item.status,
    }]
  })
  if (liveItems.length === 0) return sessions

  return sessions.map((session) => {
    if (session.id !== activeSessionId) return session

    const workById = new Map((session.parallel_work || []).map((item) => [item.id, item]))
    for (const item of liveItems) {
      const current = workById.get(item.id)
      const currentIsTerminal = current && ['completed', 'failed', 'blocked', 'cancelled'].includes(current.status)
      const nextIsTerminal = ['completed', 'failed', 'blocked', 'cancelled'].includes(item.status)
      workById.set(item.id, currentIsTerminal && !nextIsTerminal ? current : { ...current, ...item })
    }

    return { ...session, parallel_work: [...workById.values()].slice(-5) }
  })
}
